import { useMutation, useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Stack, Typography } from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import { FacilityProductsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { ReactNode, useCallback, useMemo, useState } from "react";
import invariant from "tiny-invariant";
import { formatChemicalName } from "util/chemicalName";

import { useFacility } from "../Facility/useFacility";
import { FacilityProductForm } from "./Inventory/Facility/FacilityProductForm";
import { ProductsTableProps } from "./ProductsTable";
import { useIgnoredIssues } from "./Report/useReport";
import { DELETE_FACILITY_PRODUCT, FACILITY_PRODUCTS_QUERY, FacilityProductRow } from "./schema";

const COPY_PRODUCT_INVENTORY = gql(`
  mutation CopyProductInventory($facilityId: ID!, $toYear: Int!) {
    copyFacilityProducts(facilityId: $facilityId, toYear: $toYear) {
      fromYear
    }
  }
`);

const FACILITY_INVENTORY_STATUS = gql(`
  query GetFacilityProductInventoryStatus($facilityId: ID!, $toYear: Int!) {
    facilityInventoryStatus(facilityId: $facilityId, toYear: $toYear, inventory: PRODUCT) {
      exists
      mostRecentYear
    }
  }
`);

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "productName", sort: "asc" }];
const EXCLUDE_FILTER_COLUMNS = [
  "maxQuantity",
  "averageQuantity",
  "maxAmount",
  "averageAmount",
  "issues",
];
const getItems = (data: FacilityProductsQuery) => data.facilityProducts.items;
const getCount = (data: FacilityProductsQuery) => data.facilityProducts.count;
const notSelectable = () => false;

function useFacilityProductColumns({
  facilityState,
  ignoredIssues,
  isReadOnly,
  onEdit,
  onDelete,
}: {
  facilityState: string | null | undefined;
  ignoredIssues: ReturnType<typeof useIgnoredIssues>;
  isReadOnly: boolean;
  onEdit: (facilityProductId: string) => void;
  onDelete: (row: FacilityProductRow) => void;
}) {
  return useMemo<OmnisearchGridColDef<FacilityProductRow>[]>(
    () => [
      {
        field: "productName",
        headerName: "Product",
        flex: 2,
        topAligned: true,
        renderCell: (params) => (
          <TextCellWithTooltip>{params.row.product?.name}</TextCellWithTooltip>
        ),
      },
      {
        field: "maxQuantity",
        headerName: "Max Qty",
        flex: 0.8,
        topAligned: true,
        renderCell: (params) => <TextCellWithTooltip>{params.row.maxQuantity}</TextCellWithTooltip>,
      },
      {
        field: "averageQuantity",
        headerName: "Avg Qty",
        flex: 0.8,
        topAligned: true,
        renderCell: (params) => (
          <TextCellWithTooltip>{params.row.averageQuantity}</TextCellWithTooltip>
        ),
      },
      {
        field: "chemicals",
        headerName: "Chemicals in Product",
        sortable: false,
        flex: 2,
        topAligned: true,
        renderCell(params) {
          return (
            <Box style={{ width: "100%" }}>
              {(params.row.chemicalAmounts ?? []).map((ca) => (
                <TextCellWithTooltip key={ca.chemicalId} tooltip={formatChemicalName(ca.chemical)}>
                  {ca.chemical.name}
                </TextCellWithTooltip>
              ))}
            </Box>
          );
        },
      },
      {
        field: "maxAmount",
        headerName: "Max Amount",
        flex: 1,
        sortable: false,
        topAligned: true,
        renderCell(params) {
          return (
            <Stack spacing={0} width="100%">
              {(params.row.chemicalAmounts ?? []).map((ca) => (
                <TextCellWithTooltip key={ca.chemicalId}>
                  {prettyPrintQuantity({
                    amount: ca.maxAmount,
                    unit: ca.unit,
                    state: facilityState,
                  })}
                </TextCellWithTooltip>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "averageAmount",
        headerName: "Avg Amount",
        flex: 1,
        sortable: false,
        topAligned: true,
        renderCell(params) {
          return (
            <Stack spacing={0} width="100%">
              {(params.row.chemicalAmounts ?? []).map((ca) => (
                <TextCellWithTooltip key={ca.chemicalId}>
                  {prettyPrintQuantity({
                    amount: ca.averageAmount,
                    unit: ca.unit,
                    state: facilityState,
                  })}
                </TextCellWithTooltip>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.6,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell({ row }) {
          const shouldIgnore = ignoredIssues.some((issue) =>
            row.issues?.some((i) => i.modelId === issue.modelId)
          );
          return <IssueCount issueCount={row.issues?.length} ignoreForReport={shouldIgnore} />;
        },
      },
      {
        field: "actions",
        type: "actions",
        getActions: (params) => {
          return [
            <GridActionsCellItem
              key="edit"
              label="Edit"
              icon={<Edit />}
              onClick={() => onEdit(params.row.id)}
              disabled={isReadOnly}
            >
              <Edit />
            </GridActionsCellItem>,
            <GridActionsCellItem
              key="delete"
              label="Delete"
              icon={<Delete />}
              onClick={() => onDelete(params.row)}
              disabled={isReadOnly}
            >
              <Delete />
            </GridActionsCellItem>,
          ];
        },
      },
    ],
    [facilityState, ignoredIssues, isReadOnly, onEdit, onDelete]
  );
}

export function FacilityProductsTable({ facilityId, showReportingYearPicker }: ProductsTableProps) {
  const ignoredIssues = useIgnoredIssues();
  const { isReadOnly } = useFacilityReadOnly(facilityId);
  const alerts = useAlerts();
  const reportingYear = useSelectedReportingYear();
  const { data: facilityQuery } = useFacility(facilityId);
  const facilityState = facilityQuery?.facility?.state;

  const {
    selectedId: editFacilityProductId,
    popIdFromUrl,
    pushIdToUrl,
  } = useSelectedIdInUrl("productId");

  const [deletingFacilityProduct, setDeletingFacilityProduct] = useState<
    FacilityProductRow | undefined
  >(undefined);

  const [deleteFacilityProduct, { loading: deleting }] = useMutation(DELETE_FACILITY_PRODUCT, {
    refetchQueries: [
      "InventoryFacilityProducts",
      "InventoryOverview",
      "ReportingFacilityChemicals",
      "FacilityProducts",
      "GetReportDetails",
    ],
  });

  const [copyProductInventory, { loading: isCopyingInventory }] = useMutation(
    COPY_PRODUCT_INVENTORY,
    {
      refetchQueries: ["FacilityProducts"],
      variables: { facilityId, toYear: reportingYear },
      onCompleted({ copyFacilityProducts }) {
        alerts.success(
          `Successfully copied product inventory from ${copyFacilityProducts.fromYear}`
        );
      },
      onError(err) {
        const errorCode = err.graphQLErrors?.[0]?.extensions?.code;
        if (errorCode === "NO_INVENTORY_TO_COPY") {
          alerts.info("No product inventory to copy");
        } else {
          alerts.error("There was an error copying this facility's product inventory", err);
        }
      },
    }
  );

  const { data: facilityInventoryStatus } = useQuery(FACILITY_INVENTORY_STATUS, {
    variables: { facilityId, toYear: reportingYear },
    fetchPolicy: "cache-and-network",
  });

  const handleDelete = useCallback(async () => {
    try {
      invariant(deletingFacilityProduct?.id);
      await deleteFacilityProduct({
        variables: { id: deletingFacilityProduct.id },
      });
      alerts.success("Successfully removed product from facility");
      setDeletingFacilityProduct(undefined);
    } catch (err) {
      alerts.error("An error occurred while removing product from facility", err);
    }
  }, [alerts, deleteFacilityProduct, deletingFacilityProduct, setDeletingFacilityProduct]);

  const columns = useFacilityProductColumns({
    facilityState,
    ignoredIssues,
    isReadOnly,
    onEdit: pushIdToUrl,
    onDelete: setDeletingFacilityProduct,
  });

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const defaults = `facilityId:${facilityId} reportingYear:${reportingYear}`;
      return {
        search: omnisearch ? `${omnisearch} ${defaults}` : defaults,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [facilityId, reportingYear]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<FacilityProductRow>) => pushIdToUrl(params.row.id),
    [pushIdToUrl]
  );

  const handleCopyInventory = useCallback(() => {
    copyProductInventory();
  }, [copyProductInventory]);

  const commandButtons = useMemo(() => {
    const buttons: ReactNode[] = [];
    if (showReportingYearPicker) {
      buttons.push(
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            size: "small",
          }}
          persistToLocalStorage
        />
      );
    }
    buttons.push(
      <CommandButton
        key="add"
        variant="contained"
        onClick={() => pushIdToUrl("new")}
        sx={{ width: { xs: "100%", sm: "auto" } }}
        disabled={isReadOnly}
      >
        Add Product
      </CommandButton>
    );
    return buttons;
  }, [pushIdToUrl, showReportingYearPicker, isReadOnly]);

  return (
    <>
      <OmnisearchDataGridV2
        persistenceKey="facility-products"
        columns={columns}
        query={FACILITY_PRODUCTS_QUERY}
        buildVariables={buildVariables}
        getItems={getItems}
        getCount={getCount}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={25}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        commandButtons={commandButtons}
        noRowsMessage={`No product inventory has been added to this facility for ${reportingYear}${
          !facilityInventoryStatus?.facilityInventoryStatus.exists ? " or any previous year" : ""
        }.`}
        noDataButtonText={
          !isReadOnly && facilityInventoryStatus?.facilityInventoryStatus.exists
            ? `Copy Inventory from ${facilityInventoryStatus?.facilityInventoryStatus.mostRecentYear}`
            : undefined
        }
        onNoDataButtonClick={isReadOnly ? undefined : handleCopyInventory}
        noDataButtonLoading={isCopyingInventory}
        isRowSelectable={notSelectable}
        onRowClick={handleRowClick}
        compact
        rowHeight="auto"
      />
      {deletingFacilityProduct && (
        <ConfirmDialog
          open={!!deletingFacilityProduct}
          onClose={() => setDeletingFacilityProduct(undefined)}
          onConfirm={handleDelete}
          header={"Are you sure you want to delete this product?"}
          resources={[
            {
              resourceType: "Product",
              resourceName: deletingFacilityProduct.product?.name,
            },
            {
              resourceType: "Reporting Year",
              resourceName: reportingYear,
            },
          ]}
          info={DeleteProductMessage({
            productName: deletingFacilityProduct.product?.name,
            forMeasurements: false,
          })}
          loading={deleting}
        />
      )}
      {editFacilityProductId && (
        <FacilityProductForm
          facilityProductId={editFacilityProductId}
          facilityId={facilityId}
          reportingYear={reportingYear}
          onClose={popIdFromUrl}
        />
      )}
    </>
  );
}

export function DeleteProductMessage({
  productName,
  forMeasurements,
}: {
  productName?: string;
  forMeasurements: boolean;
}) {
  return (
    <>
      <Typography component="span">
        This action will:
        <ul>
          <li>Remove {productName} from your product inventory</li>
          {forMeasurements && <li>Remove all daily measurements for this product</li>}
          <li>
            Reduce your total reported chemical quantities by the amount this product contributed
          </li>
        </ul>
      </Typography>
      <Typography component="span">
        This action will <b>not:</b>
        <ul>
          <li>Change the entry for {productName} in your product catalog</li>
          <li>Affect chemical inventory reported as Other Chemicals</li>
          <li>Affect previous reporting years</li>
        </ul>
      </Typography>
    </>
  );
}
